import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { Sparkles, LogOut, Menu } from "lucide-react";

const NAV_ITEMS = [
  { to: "/ask", label: "Ask", testid: "nav-oracle" },
  { to: "/tools", label: "Tools", testid: "nav-tools" },
  { to: "/history", label: "Readings", testid: "nav-history" },
  { to: "/pricing", label: "Credits", testid: "nav-pricing" },
];

const CONTACT_EMAIL = "numero.auracean@gmail.com";
const CONTACT_HREF = `mailto:${CONTACT_EMAIL}?subject=Numero%20by%20Auracean%20-%20Query`;

export const Header = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/ask";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    nav("/");
  };

  return (
    <header className="glass sticky top-0 z-50 border-b border-white/5">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4">
        <Link to="/" className="flex items-center gap-2 group shrink-0" data-testid="logo-link">
          <span className="grid h-8 w-8 place-items-center rounded-full border border-gold/60 text-gold font-serif text-lg group-hover:shadow-gold transition-shadow duration-300">
            ✦
          </span>
          <span className="font-serif text-lg sm:text-xl tracking-tight">Auracean</span>
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-neutral-500 ml-1">Numerology</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-300">
          {NAV_ITEMS.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={`hover:text-white transition-colors duration-200 ${loc.pathname === it.to ? "text-white" : ""}`}
            >{it.label}</Link>
          ))}
          <a
            href={CONTACT_HREF}
            data-testid="nav-contact"
            className="hover:text-white transition-colors duration-200"
          >Contact us</a>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {user ? (
            <>
              <div
                data-testid="credit-balance"
                className="flex items-center gap-1.5 sm:gap-2 rounded-full border border-white/10 bg-black/40 px-3 sm:px-4 py-1.5 text-sm"
              >
                <Sparkles className="h-4 w-4 text-gold" />
                <span className="font-medium">{user.credits}</span>
                <span className="text-neutral-500 hidden sm:inline">credits</span>
              </div>
              {user.picture && (
                <img
                  src={user.picture}
                  alt=""
                  className="hidden md:block h-8 w-8 rounded-full border border-white/10 object-cover"
                  referrerPolicy="no-referrer"
                />
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="hidden md:inline-flex rounded-full hover:bg-white/5"
                title="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              onClick={handleLogin}
              data-testid="login-btn"
              className="rounded-full bg-gold text-black hover:bg-[#F3E5AB] transition-colors duration-200 px-4 sm:px-5 h-9"
            >
              Sign in
            </Button>
          )}

          {/* Mobile menu trigger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-btn"
                className="md:hidden rounded-full hover:bg-white/5 h-9 w-9"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#0A0A0C] border-white/10 text-white w-[280px] sm:w-[320px] p-0"
              data-testid="mobile-menu"
            >
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="flex flex-col h-full">
                <div className="p-6 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <span className="grid h-8 w-8 place-items-center rounded-full border border-gold/60 text-gold font-serif">✦</span>
                    <span className="font-serif text-lg">Auracean</span>
                  </div>
                  {user && (
                    <div className="mt-5 flex items-center gap-3">
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt=""
                          className="h-10 w-10 rounded-full border border-white/10 object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full border border-white/10 bg-white/5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{user.name || user.email}</div>
                        <div className="text-xs text-gold flex items-center gap-1 mt-0.5">
                          <Sparkles className="h-3 w-3" /> {user.credits} credits
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <nav className="flex-1 p-4 space-y-1">
                  {NAV_ITEMS.map((it) => {
                    const active = loc.pathname === it.to;
                    return (
                      <Link
                        key={it.to}
                        to={it.to}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-${it.testid}`}
                        className={`block rounded-xl px-4 py-3 text-base transition-colors duration-200 ${
                          active
                            ? "bg-gold/10 text-gold border border-gold/30"
                            : "text-neutral-200 hover:bg-white/5"
                        }`}
                      >{it.label}</Link>
                    );
                  })}
                  <a
                    href={CONTACT_HREF}
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-contact"
                    className="block rounded-xl px-4 py-3 text-base transition-colors duration-200 text-neutral-200 hover:bg-white/5"
                  >Contact us</a>
                </nav>

                {user && (
                  <div className="p-4 border-t border-white/5">
                    <Button
                      variant="ghost"
                      onClick={handleLogout}
                      data-testid="mobile-logout-btn"
                      className="w-full h-12 justify-start rounded-xl text-neutral-300 hover:bg-white/5 hover:text-white"
                    >
                      <LogOut className="h-4 w-4 mr-3" /> Sign out
                    </Button>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
